import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Loader2, CheckCircle, PhoneCall } from "lucide-react";
import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL || ""}/api`,
});

const STATUS_LABELS = {
  preparing: "Preparazione assistente AI…",
  calling: "Stai per ricevere la chiamata demo…",
  in_call: "In conversazione con l'AI",
  ended: "Elaborazione riassunto…",
  completed: "Demo completata",
  busy: "Numero occupato — riprova tra poco",
  failed: "Chiamata fallita — riprova",
  "no-answer": "Nessuna risposta — riprova",
  unknown: "Connessione…",
};

const STATUS_COLORS = {
  preparing: "text-amber-600",
  calling: "text-blue-600",
  busy: "text-red-500",
  failed: "text-red-500",
  "no-answer": "text-red-500",
  in_call: "text-green-600",
  ended: "text-amber-600",
  completed: "text-green-700",
  unknown: "text-gray-500",
};

const FAILED_STATUSES = ["busy", "failed", "no-answer", "canceled"];

export default function PhoneCallDemo() {
  const location = useLocation();
  const [step, setStep] = useState("loading");
  const [callStatus, setCallStatus] = useState("preparing");
  const [companyName, setCompanyName] = useState("");
  const [summary, setSummary] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const pollRef = useRef(null);
  const startedRef = useRef(false);

  const startPolling = (sid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/phone-demo/${sid}/status`);
        const d = res.data;
        setCallStatus(d.status);
        if (d.company_name && d.company_name !== "Non specificato") setCompanyName(d.company_name);
        if (d.status === "completed") {
          clearInterval(pollRef.current);
          setSummary(d.summary);
          setStep("done");
        } else if (FAILED_STATUSES.includes(d.status)) {
          clearInterval(pollRef.current);
          setStep("failed");
        }
      } catch (e) {
        console.error(e);
      }
    }, 3000);
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const state = location.state || {};
    const ws = state.website || "";
    const ph = state.phone || "";
    const em = state.email || "";
    const vo = state.voice || "f";

    setWebsite(ws);
    setPhone(ph);

    if (!ws || !ph) {
      setStep("error");
      setErrorMsg("Dati mancanti. Torna alla homepage e riprova.");
      return;
    }

    setStep("loading");
    setCallStatus("preparing");

    api.post("/phone-demo/start", { website: ws, phone: ph, email: em, voice: vo })
      .then((res) => {
        const sid = res.data.session_id;
        setStep("calling");
        setCallStatus("calling");
        startPolling(sid);
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || "Errore avvio chiamata. Riprova.";
        setErrorMsg(msg);
        setStep("error");
      });

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleRetry = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-3xl font-black tracking-tight mb-1">
            24<span className="text-[#D97757]">Vox</span>
          </div>
          <p className="text-sm text-gray-500 italic">la tua voce, sempre…</p>
        </div>

        {/* LOADING — analisi sito */}
        {step === "loading" && (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-10 shadow-sm text-center">
            <Loader2 className="w-10 h-10 animate-spin text-[#D97757] mx-auto mb-4" />
            <p className="font-semibold text-gray-700">Analizzo il tuo profilo…</p>
            <p className="text-sm text-gray-400 mt-1">Costruiamo il tuo assistente AI personalizzato</p>
          </div>
        )}

        {/* CALLING / IN_CALL */}
        {step === "calling" && (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-10 shadow-sm text-center">
            <div className="relative mx-auto w-20 h-20 mb-6">
              <div className="absolute inset-0 bg-[#D97757]/20 rounded-full animate-ping" />
              <div className="relative flex items-center justify-center w-20 h-20 bg-[#D97757]/10 rounded-full">
                <Phone className="w-8 h-8 text-[#D97757]" />
              </div>
            </div>
            {companyName && (
              <p className="text-xs uppercase tracking-widest text-[#D97757] font-semibold mb-1">
                {companyName}
              </p>
            )}
            <p className={`font-bold text-lg mb-1 ${STATUS_COLORS[callStatus] || "text-gray-700"}`}>
              {STATUS_LABELS[callStatus] || "…"}
            </p>
            <p className="text-sm text-gray-400">
              {callStatus === "in_call" ? "Parla liberamente con il tuo assistente" : "Attendere…"}
            </p>
            <div className="mt-6 flex justify-center gap-1">
              {[0, 1, 2].map((i) => (
                <span key={i} className="w-2 h-2 bg-[#D97757] rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
            <button
              onClick={handleRetry}
              className="mt-6 text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Annulla
            </button>
          </div>
        )}

        {/* FAILED */}
        {step === "failed" && (
          <div className="bg-white border border-red-100 rounded-2xl p-10 shadow-sm text-center">
            <div className="flex items-center justify-center w-20 h-20 bg-red-50 rounded-full mx-auto mb-6">
              <PhoneCall className="w-8 h-8 text-red-400" />
            </div>
            <p className="font-bold text-lg text-red-500 mb-1">
              {STATUS_LABELS[callStatus] || "Chiamata non riuscita"}
            </p>
            <p className="text-sm text-gray-400 mb-6">Controlla il numero e riprova.</p>
            <button
              onClick={handleRetry}
              className="w-full bg-[#D97757] hover:bg-[#c4684a] text-white font-bold py-3 rounded-xl transition"
            >
              ← Riprova
            </button>
          </div>
        )}

        {/* ERROR */}
        {step === "error" && (
          <div className="bg-white border border-red-100 rounded-2xl p-10 shadow-sm text-center">
            <p className="font-bold text-lg text-red-500 mb-2">Qualcosa è andato storto</p>
            <p className="text-sm text-gray-400 mb-6">{errorMsg}</p>
            <button
              onClick={handleRetry}
              className="w-full bg-[#D97757] hover:bg-[#c4684a] text-white font-bold py-3 rounded-xl transition"
            >
              ← Torna alla homepage
            </button>
          </div>
        )}

        {/* DONE */}
        {step === "done" && summary && (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-8 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <h2 className="font-bold text-lg">Demo completata</h2>
            </div>
            {companyName && (
              <p className="text-xs uppercase tracking-widest text-[#D97757] font-semibold mb-4">
                {companyName}
              </p>
            )}
            <div className="bg-[#F0EBE1] rounded-xl p-4 mb-4">
              <p className="text-xs text-gray-500 italic mb-1">Riassunto</p>
              <p className="text-sm leading-relaxed">{summary.riassunto_breve || "—"}</p>
            </div>
            <div className="space-y-2">
              {[
                ["Cliente", summary.nome_cliente],
                ["Motivo", summary.motivo_chiamata],
                ["Contatti", summary.contatti_lasciati],
                ["Azione", summary.azione_richiesta],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k} className="flex gap-2 text-sm">
                    <span className="text-gray-400 w-20 shrink-0">{k}</span>
                    <span className="text-gray-800">{v}</span>
                  </div>
                ))}
            </div>
            <p className="text-xs text-gray-400 mt-6 text-center">
              Riassunto inviato via email ✓
            </p>
            <button
              onClick={handleRetry}
              className="mt-4 w-full border border-[#E5E0D8] text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-xl text-sm transition"
            >
              ← Prova con un altro sito
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
